import * as clipboard from "clipboard-polyfill";
import hljs from "highlight.js/lib/core";
import debounce from "lodash.debounce";
import { common } from "lowlight";
import type MarkdownIt from "markdown-it";
import { v4 as uuidv4 } from "uuid";

export type MarkdownItCodeWrapOptions = {
  sourceLabel?: string;
  copyLabel?: string;
  previewLabel?: string;
  reloadLabel?: string;
};

const SELECTORS = {
  COPY_BUTTON: ".button-copy",
  COPY_BUTTON_DEFAULT: ".button-copy-default",
  COPY_BUTTON_STATUS: ".button-copy-status",
  PREVIEW_BUTTON: ".button-preview",
  RELOAD_BUTTON: ".button-reload",
  CODE_BLOCK: ".md-code-block",
  CODE_BLOCK_IFRAME: ".md-code-block__iframe",
};

export function markDownItCodeWrap(
  md: MarkdownIt,
  {
    sourceLabel = "Source",
    copyLabel = "Copy",
    previewLabel = "Preview",
    reloadLabel = "Reload",
  }: MarkdownItCodeWrapOptions = {}
) {
  Object.entries(common).forEach(([lang, value]) => {
    hljs.registerLanguage(lang, value);
  });

  const parseLanguage = (info: string): string => {
    const RE = /^(?<language>\w{0,15})/;
    const matched = RE.exec(info);
    return matched?.groups?.language || "txt";
  };

  const injectHead = (source: string) => {
    const injectedCode = `
      <script>
      function sendSize() {
        window.parent.postMessage(
          {
            type: "resize",
            width: document.body.scrollWidth,
            height: document.body.scrollHeight
          },
          "*"
        )
      }

      // when this window loads or resizes …
      // call our sendSize function
      window.addEventListener("load", sendSize)
      window.addEventListener("resize", sendSize)
      </script>
      <script>
      (function() {
        const originalConsole = window.console;
        window.console = {
          log: (...args) => {
            originalConsole.log(...args);
            window.parent.postMessage({ type: 'console', message: args.join(' ') }, '*');
          },
          error: (...args) => {
            originalConsole.error(...args);
            window.parent.postMessage({ type: 'console', message: 'Error: ' + args.join(' ') }, '*');
          },
          warn: (...args) => {
            originalConsole.warn(...args);
            window.parent.postMessage({ type: 'console', message: 'Warning: ' + args.join(' ') }, '*');
          }
        };

        window.addEventListener('error', (event) => {
          window.parent.postMessage({ type: 'console', message: 'Uncaught Error: ' + event.message }, '*');
        });
      })();
      </script>
    `.trim();
    return source.replace(/<\/head>/, `${injectedCode}</head>`);
  };

  const resize = (el: HTMLElement, width: number, height: number) => {
    el.setAttribute("width", `${width}px`);
    el.setAttribute("height", `${height}px`);
  };
  const debouncedResize = debounce(
    (el: HTMLElement, width: number, height: number) =>
      resize(el, width, height),
    300
  );

  const getSource = (codeBlockElement: HTMLElement) => {
    return (codeBlockElement.querySelector("pre code") as HTMLElement)
      ?.innerText;
  };

  const updateCopyButtonStatus = (
    codeBlockElement: HTMLElement,
    success: boolean
  ) => {
    const label = codeBlockElement.querySelector(
      SELECTORS.COPY_BUTTON_DEFAULT
    ) as HTMLElement;
    const status = codeBlockElement.querySelector(
      SELECTORS.COPY_BUTTON_STATUS
    ) as HTMLElement;
    if (label && status) {
      label.style.display = "none";
      status.style.display = "block";
      status.textContent = success ? "Copied!" : "Failed to copy";
      setTimeout(() => {
        status.style.display = "none";
        label.style.display = "block";
      }, 1000);
    }
  };

  const handleCopy = (e: Event) => {
    const copyButton = (e.target as HTMLElement).closest(SELECTORS.COPY_BUTTON);
    if (!copyButton) return;

    const codeBlockElement = copyButton.closest(
      SELECTORS.CODE_BLOCK
    ) as HTMLElement;

    const str = getSource(codeBlockElement);
    const clipboardItem = new clipboard.ClipboardItem({
      "text/html": new Blob([str], { type: "text/html" }),
      "text/plain": new Blob([str], { type: "text/plain" }),
    });
    clipboard.write([clipboardItem]);
    updateCopyButtonStatus(codeBlockElement, true);
  };

  enum MessageType {
    Resize = "resize",
    Console = "console",
  }

  const handleIframeMessage = (
    event: MessageEvent,
    iframeElement: HTMLIFrameElement
  ) => {
    if (event.data.type === MessageType.Resize) {
      debouncedResize(iframeElement, event.data.width, event.data.height);
    } else if (event.data.type === MessageType.Console) {
      // eslint-disable-next-line no-console
      console.log(event.data.message);
    }
  };

  const loadIFrameSrc = (codeBlockElement: HTMLElement) => {
    const iframeElement = codeBlockElement.querySelector(
      SELECTORS.CODE_BLOCK_IFRAME
    ) as HTMLIFrameElement;
    const str = getSource(codeBlockElement);
    if (iframeElement) {
      iframeElement.srcdoc = injectHead(str);

      window.addEventListener("message", (event) =>
        handleIframeMessage(event, iframeElement)
      );
    }
  };

  const handlePreview = (e: Event) => {
    const button = (e.target as HTMLElement).closest(SELECTORS.PREVIEW_BUTTON);
    if (!button) return;

    const codeBlockElement = button.closest(
      SELECTORS.CODE_BLOCK
    ) as HTMLElement;
    loadIFrameSrc(codeBlockElement);
  };

  const handleReload = (e: Event) => {
    const button = (e.target as HTMLElement).closest(SELECTORS.RELOAD_BUTTON);
    if (!button) return;

    const codeBlockElement = button.closest(
      SELECTORS.CODE_BLOCK
    ) as HTMLElement;
    loadIFrameSrc(codeBlockElement);
  };

  document.addEventListener("click", async (e) => {
    handleCopy(e);
    handlePreview(e);
    handleReload(e);
  });

  const generateCodeBlockHTML = (
    id: string,
    language: string,
    result: string,
    style: string
  ) => {
    return `
      <div class="md-code-block">
        <div class="md-code-block__header hide-for-print">
          <ul class="nav align-items-center" id="myTab-${id}" role="tablist">
            <li class="nav-item mb-0" role="presentation" style="${style}">
              <button class="nav-link btn-flex btn-sm active button-source" data-bs-toggle="tab" data-bs-target="#source-${id}" type="button" role="tab" aria-controls="source" aria-selected="true">${sourceLabel}</button>
            </li>
            <li class="nav-item mb-0" role="presentation" style="${style}">
              <button class="nav-link btn-flex btn-sm button-preview" data-bs-toggle="tab" data-bs-target="#preview-${id}" type="button" role="tab" aria-controls="preview" aria-selected="false">${previewLabel}</button>
            </li>
            <li class="nav-item mb-0  ms-auto" role="presentation" style="${style}">
              <button class="nav-link btn-flex btn-sm button-reload" type="button">
                <i class="bi bi-arrow-clockwise me-1"></i> ${reloadLabel}
              </button>
            </li>
            <li class="nav-item mb-0">
              <button class="nav-link btn-flex btn-sm button-copy">
                <span class="button-copy-default">
                  <i class="bi bi-copy me-1"></i>${copyLabel}
                </span>
                <span class="button-copy-status" style="display: none">
                  <i class="bi bi-check-lg me-1"></i>
                  Kopiert!
                </span>
              </button>
            </li>
          </ul>
        </div>
        <div class="tab-content" id="myTabContent-${id}">
          <div class="tab-pane show active" id="source-${id}" role="tabpanel" aria-labelledby="source-${id}">
            <div class="md-code-block__source">
              <pre><code class="language-${language}">${result}</code></pre>
            </div>
          </div>
          <div class="tab-pane" id="preview-${id}" role="tabpanel" aria-labelledby="preview">
            <div class="md-code-block__preview">
              <iframe class="md-code-block__iframe" sandbox="allow-scripts" marginheight="0" frameborder="0" style="width: 100%; min-height: 500px; border: none" >
              </iframe>
            </div>
          </div>
        </div>
      </div>
    `.trim();
  };

  // @ts-ignore no idea how to get the types
  md.renderer.rules.fence = (tokens: MarkdownIt.Token[], idx: number) => {
    const { info, content } = tokens[idx];
    const language = parseLanguage(info);
    const source = content;
    const result = hljs.highlightAuto(source.trim()).value.trim();
    const style = language === "html" ? "" : "display: none";
    const id = uuidv4();

    return generateCodeBlockHTML(id, language, result, style);
  };
}
